// Normalised (0–1) path coordinates for each shape.
// Structure: M(x,y) + C(x1,y1,x2,y2,x,y)×8 + L(x,y) = 52 values, x at even indices, y at odd.
const MOBILE_NORM = [
  0.04226, 0.99598,                                                     // M
  0.00837, 0.99487,  0.0,     0.98921,  0.0,     0.96166,              // C1
  0.01214, 0.71121,  0.01423, 0.27988,  0.0,     0.03636,              // C2
  0.00146, 0.01228,  0.00942, 0.00425,  0.03556, 0.00394,              // C3
  0.35441, 0.01464,  0.65212, 0.01306,  0.93999, 0.0,                  // C4
  0.96468, 0.0,      0.97284, 0.00582,  0.97954, 0.02330,              // C5
  1.00004, 0.08060,  1.00987, 0.93584,  0.98623, 0.97347,              // C6
  0.97933, 0.98968,  0.95736, 0.98921,  0.92556, 0.99046,              // C7
  0.60880, 1.00149,  0.31529, 1.00243,  0.04247, 0.99597,              // C8
  0.04226, 0.99598,                                                     // L
];

const DESKTOP_NORM = [
  // Reordered to start at bottom-left (matching mobile) so interpolation morphs corner-to-corner.
  0.03636, 1.0,                                                        // M  ← BL
  0.01228, 0.99853,  0.00425, 0.99059,  0.00394, 0.96443,             // C1 ← L
  0.01464, 0.64559,  0.01307, 0.34788,  0.0,     0.06001,             // C2 ← UL
  0.0,     0.03528,  0.00582, 0.02716,  0.02330, 0.02046,             // C3 ← TL
  0.08060, -0.00004, 0.93585, -0.00987, 0.97346, 0.01377,             // C4 ← T
  0.98967, 0.02067,  0.98921, 0.04264,  0.99047, 0.07444,             // C5 ← TR
  1.00149, 0.39119,  1.00243, 0.68472,  0.99598, 0.95753,             // C6 ← R
  0.99487, 0.99163,  0.98921, 1.0,      0.96166, 1.0,                 // C7 ← BR
  0.71121, 0.98787,  0.27989, 0.98577,  0.03636, 1.0,                 // C8 ← BL
  0.03636, 1.0,                                                        // L
];

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

// Scale normalised coords to pixel dimensions and serialise to a path string.
function buildPath(norm: number[], w: number, h: number): string {
  let i = 0;
  const s = (scale: number) => (norm[i++] * scale).toFixed(2);
  const xy = () => `${s(w)} ${s(h)}`;
  return (
    `M${xy()}` +
    `C${xy()} ${xy()} ${xy()}` +
    `C${xy()} ${xy()} ${xy()}` +
    `C${xy()} ${xy()} ${xy()}` +
    `C${xy()} ${xy()} ${xy()}` +
    `C${xy()} ${xy()} ${xy()}` +
    `C${xy()} ${xy()} ${xy()}` +
    `C${xy()} ${xy()} ${xy()}` +
    `C${xy()} ${xy()} ${xy()}` +
    `L${xy()}Z`
  );
}

export class BannerCard {
  private readonly BREAKPOINT = 800;
  private readonly DURATION = 400; // ms — keep in sync with aspect-ratio transition in SCSS

  private currentT: number;
  private rafId: number | null = null;

  constructor() {
    this.currentT = window.innerWidth >= this.BREAKPOINT ? 1 : 0;
    this.init();
  }

  public init(): void {
    const mediaEls = document.querySelectorAll<HTMLElement>('.banner-card__media');
    if (!mediaEls.length) return;

    let lastLayout = this.currentT === 1 ? 'desktop' : 'mobile';

    const observer = new ResizeObserver(() => {
      const next = window.innerWidth >= this.BREAKPOINT ? 'desktop' : 'mobile';
      if (next !== lastLayout) {
        lastLayout = next;
        mediaEls.forEach((el) => this.morph(el, next === 'desktop' ? 1 : 0));
      }
    });

    observer.observe(document.documentElement);
  }

  private morph(el: HTMLElement, targetT: number): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);

    const startT = this.currentT;
    const startTime = performance.now();

    const tick = (now: number) => {
      const raw = Math.min((now - startTime) / this.DURATION, 1);
      this.currentT = startT + (targetT - startT) * easeInOut(raw);

      const lerped = MOBILE_NORM.map((v, i) => v + (DESKTOP_NORM[i] - v) * this.currentT);
      const { width, height } = el.getBoundingClientRect();
      el.style.clipPath = `path('${buildPath(lerped, width, height)}')`;

      if (raw < 1) {
        this.rafId = requestAnimationFrame(tick);
      } else {
        this.currentT = targetT;
        this.rafId = null;
        el.style.clipPath = ''; // hand back to CSS url() clip-path
      }
    };

    this.rafId = requestAnimationFrame(tick);
  }
}
